/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { LoanTypeOption, fetchLoanTypes } from "./loanSurety.service";

const LoanSuretySelection: React.FC = () => {
  const navigate = useNavigate();
  const [loanTypes, setLoanTypes] = useState<LoanTypeOption[]>([]);
  const [loanType, setLoanType] = useState("");
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);

  const loadLoanTypes = useCallback(async () => {
    setLoading(true);
    try {
      const items = await fetchLoanTypes();
      setLoanTypes(items);
      if (!items.length) {
        toast.info("No loan types found");
      }
    } catch (err: any) {
      setLoanTypes([]);
      toast.error(err?.message || "Failed to load loan types");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLoanTypes();
  }, [loadLoanTypes]);

  const handleClear = () => {
    setLoanType("");
    setTouched(false);
  };

  const handleDisplay = () => {
    setTouched(true);
    if (!loanType) {
      toast.error("Loan type is required");
      return;
    }
    const selected = loanTypes.find((item) => item.value === loanType);
    navigate(
      `${all_routes.loanSuretyStatus}?loanType=${encodeURIComponent(
        loanType
      )}&loanLabel=${encodeURIComponent(selected?.label || "")}`
    );
  };

  return (
    <div className="page-wrapper">
      <style>{selectionStyles}</style>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">
                  Please enter the selection criteria
                </h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.employeeDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Tax & Loan</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Loan Surety Status Check
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>

        <div className="card loan-filter-card">
          <div className="card-body">
            <div className="loan-info-box mb-3">
              <h6 className="mb-1 fw-bold">Search Loan type</h6>
              <p className="mb-0 text-muted fs-13">
                Select a loan type from the available options to view details
              </p>
            </div>

            <div className="row g-3 align-items-end">
              <div className="col-sm-8 col-md-6 col-lg-4">
                <label className="form-label fw-medium mb-1">
                  <i className="ti ti-building-bank me-1 text-primary" />
                  Loan Type
                </label>
                <select
                  className={`form-select ${
                    touched && !loanType ? "is-invalid" : ""
                  }`}
                  value={loanType}
                  onChange={(e) => setLoanType(e.target.value)}
                  disabled={loading}
                >
                  <option value="">
                    {loading ? "Loading loan types..." : "Select loan type"}
                  </option>
                  {loanTypes.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.value} - {item.label}
                    </option>
                  ))}
                </select>
                {touched && !loanType && (
                  <div className="invalid-feedback d-block">
                    Loan type is required
                  </div>
                )}
              </div>
              <div className="col-sm-4 col-md-6 col-lg-8 d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleClear}
                  disabled={loading}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="btn btn-primary d-inline-flex align-items-center gap-1"
                  onClick={handleDisplay}
                  disabled={loading}
                >
                  <i className="ti ti-search" />
                  Display Data
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const selectionStyles = `
.loan-filter-card { border: none; box-shadow: 0 2px 12px rgba(0,0,0,0.04); }
.loan-info-box {
  background: #F4F8FF;
  border-radius: 12px;
  padding: 14px 16px;
  border-left: 4px solid var(--primary, #dc2626);
}
.fs-13 { font-size: 13px; }
`;

export default LoanSuretySelection;
export { default as LoanSuretyStatus } from "./LoanSuretyStatus";
